import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserCircle, ChevronDown, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from './LanguageSelector';
import { Logo } from './Logo';
import { MobileMenu } from './MobileMenu';

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const shouldUseTransparentBg = isHomePage && !isScrolled;

  return (
    <>
      <nav className={`fixed w-full z-40 transition-all duration-300 ${
        shouldUseTransparentBg ? 'bg-transparent' : 'bg-white shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo isLight={shouldUseTransparentBg} />

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className={`lg:hidden p-2 rounded-md ${
                shouldUseTransparentBg ? 'text-white' : 'text-gray-700'
              }`}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="hidden lg:flex items-center space-x-1">
              {/* Home */}
              <Link
                to="/"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${shouldUseTransparentBg
                    ? 'text-gray-100 hover:text-white'
                    : 'text-gray-700 hover:text-blue-600'
                  } ${location.pathname === '/' ? 'font-bold' : ''}`}
              >
                {t('nav.home')}
              </Link>

              {/* VPS Hosting */}
              <Link
                to="/vps"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${shouldUseTransparentBg
                    ? 'text-gray-100 hover:text-white'
                    : 'text-gray-700 hover:text-blue-600'
                  } ${location.pathname === '/vps' ? 'font-bold' : ''}`}
              >
                {t('nav.vps')}
              </Link>

              {/* Game Servers */}
              <Link
                to="/game-hosting"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${shouldUseTransparentBg
                    ? 'text-gray-100 hover:text-white'
                    : 'text-gray-700 hover:text-blue-600'
                  } ${location.pathname === '/game-hosting' ? 'font-bold' : ''}`}
              >
                {t('nav.gameHosting')}
              </Link>

              {/* Dedicated Servers */}
              <Link
                to="/dedicated"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${shouldUseTransparentBg
                    ? 'text-gray-100 hover:text-white'
                    : 'text-gray-700 hover:text-blue-600'
                  } ${location.pathname === '/dedicated' ? 'font-bold' : ''}`}
              >
                {t('nav.dedicated')}
              </Link>

              {/* Web Hosting */}
              <Link
                to="/web-hosting"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${shouldUseTransparentBg
                    ? 'text-gray-100 hover:text-white'
                    : 'text-gray-700 hover:text-blue-600'
                  } ${location.pathname === '/web-hosting' ? 'font-bold' : ''}`}
              >
                {t('nav.webHosting')}
              </Link>

              {/* Business Solutions */}
              <div className="relative group">
                <button
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-1
                    ${shouldUseTransparentBg
                      ? 'text-gray-100 hover:text-white'
                      : 'text-gray-700 hover:text-blue-600'
                    }`}
                >
                  <span>{t('nav.businessSolutions')}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                <div className="absolute left-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-500 ease-in-out transform -translate-y-1 group-hover:translate-y-0">
                  <div className="bg-white rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 py-1">
                    <Link
                      to="/network"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {t('nav.network')}
                    </Link>
                    <Link
                      to="/colocation"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {t('nav.colocation')}
                    </Link>
                    <Link
                      to="/ip-transit"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {t('nav.ipTransit')}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Contact Us */}
              <Link
                to="/contact"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${shouldUseTransparentBg
                    ? 'text-gray-100 hover:text-white'
                    : 'text-gray-700 hover:text-blue-600'
                  } ${location.pathname === '/contact' ? 'font-bold' : ''}`}
              >
                {t('nav.contact')}
              </Link>

              <LanguageSelector />

              <a
                href="https://my.synthohosting.net/login.php"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium
                  ${shouldUseTransparentBg
                    ? 'bg-white text-blue-600 hover:bg-gray-100'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
              >
                <UserCircle className="w-5 h-5" />
                <span>{t('nav.clientArea')}</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
    </>
  );
}
